import { Injectable } from '@angular/core';

/**
 * Términos del glosario centralizado para la UI de estimaciones v3.
 * Se añaden aquí para que el componente <app-info-tooltip> pueda
 * resolver textos didácticos por clave sin que cada sitio repita el string.
 *
 * Cuando se introduzca i18n, este map pasará a las claves de traducción.
 */
export type GlossaryTerm =
  | 'p50'
  | 'contingency'
  | 'total_with_contingency'
  | 'phantom_tasks'
  | 'confidence'
  | 'basis'
  | 'project_type_evolutionary'
  | 'project_type_new'
  | 'project_type_recurring'
  | 'project_type_maintenance'
  | 'calibration_applied'
  | 'calibration_ratio'
  | 'confidence_marker_low'
  | 'confidence_marker_medium'
  | 'range_bar'
  | 'samples_used'
  | 'phantom_tasks_detected'
  | 'outliers_excluded'
  | 'ai_assistance_observed_factor'
  | 'reasoning_trace'
  | 'self_check'
  | 'prompt_version'
  | 'rollup_reconciliation'
  | 'mmre'
  | 'pred25'
  | 'drift'
  | 'prompt_injection';

@Injectable({ providedIn: 'root' })
export class GlossaryService {

  private static readonly TERMS: Record<GlossaryTerm, string> = {
    p50: 'Estimación mediana en horas. Punto donde el 50% de proyectos similares se completaron por debajo y 50% por encima.',

    contingency: 'Buffer de horas reservado para imprevistos basado en la incertidumbre del proyecto.',

    total_with_contingency: 'Suma final que se factura al cliente: P50 + buffer de contingencia.',

    phantom_tasks: 'Horas asignadas a tareas que aparecieron en proyectos similares como hotfix, scope creep o soporte. Aún no las pediste, pero el histórico sugiere que probablemente las necesites.',

    confidence: 'Nivel de fiabilidad de la estimación. Alta: múltiples análogos calibrados con muestra suficiente. Media: calibración aplicada con margen de variación. Baja: muestra reducida o análogos poco similares.',

    basis: 'De dónde sale la estimación. Calibrado con histórico: usa proyectos análogos reales. Estándar industria: benchmarks sectoriales. Mixto: combinación.',

    project_type_evolutionary: 'Tipo de proyecto detectado por IA. Evolutivo: extensión sobre una base existente; estima diferenciales + integración + regresión.',

    project_type_new: 'Tipo de proyecto detectado por IA. Nuevo: proyecto desde cero; descomposición exhaustiva con contingencia mayor.',

    project_type_recurring: 'Tipo de proyecto detectado por IA. Recurrente: patrón repetido; instancia el patrón histórico más cercano y estima solo desviaciones.',

    project_type_maintenance: 'Tipo de proyecto detectado por IA. Mantenimiento: parche o soporte; foco en throughput histórico.',

    calibration_applied: 'Ajuste numérico aplicado a la estimación basado en cuánto se desviaron en proyectos pasados las estimaciones iniciales respecto al esfuerzo real.',

    calibration_ratio: 'Cuánto se desvió, en mediana, el esfuerzo real respecto al estimado en proyectos análogos. >1.0 = se subestimó (tardó más). <1.0 = se sobreestimó.',

    confidence_marker_low: 'Tarea con confianza baja. Estimación con incertidumbre alta — considera revisar el rango.',

    confidence_marker_medium: 'Tarea con confianza media. Estimación razonable pero con margen de variación.',

    range_bar: 'Rango P10-P50-P90: escenario optimista, mediana, escenario pesimista.',

    samples_used: 'Número de proyectos análogos utilizados para calibrar la estimación.',

    phantom_tasks_detected: 'Tareas preventivas detectadas en proyectos análogos (hotfix, scope creep, soporte) e incluidas preventivamente en esta estimación.',

    outliers_excluded: 'Proyectos descartados de la calibración por valores anómalos.',

    ai_assistance_observed_factor: 'Factor de aceleración por IA observado en proyectos previos del equipo. Reemplaza al factor sectorial cuando hay datos propios suficientes.',

    reasoning_trace: 'Resumen del razonamiento del modelo: cómo clasificó el proyecto, qué muestras usó, qué decisiones tomó. Útil para auditar.',

    self_check: 'Auto-verificaciones que el modelo ejecutó antes de devolver la estimación: SDLC presente, deduplicación aplicada, guardrails respetados, etc.',

    prompt_version: 'Versión del prompt que generó esta estimación. Útil para A/B testing y comparativa de calidad entre revisiones.',

    rollup_reconciliation: 'Cuando el modelo devuelve un total de horas que no coincide exactamente con la suma real de las tareas, el backend lo recalcula automáticamente desde las tareas. Este bloque muestra ambos valores para auditoría.',

    mmre: 'Mean Magnitude of Relative Error: error medio relativo entre lo estimado y lo realmente ejecutado en tareas con histórico. Menor es mejor. Estándar industria: <0.25 excelente, <0.50 aceptable.',

    pred25: 'PRED(25): porcentaje de tareas cuya estimación se desvió ≤25% del esfuerzo real. Mayor es mejor. Estándar industria: ≥75% indica calidad alta.',

    drift: 'Deriva temporal de la calidad del modelo. Detecta cuándo MMRE empeora o PRED25 cae a lo largo de las semanas. Señal para revisar prompts, calibración o muestra histórica.',

    prompt_injection: 'Intento del usuario de manipular el comportamiento del modelo mediante instrucciones malintencionadas embebidas en el prompt. El sistema las detecta y bloquea automáticamente.'
  };

  /**
   * Obtiene el texto didáctico de un término del glosario.
   * Devuelve cadena vacía si el término no existe (fail-safe).
   */
  getText(term: GlossaryTerm): string {
    return GlossaryService.TERMS[term] ?? '';
  }

  /** True si la clave existe en el glosario. */
  has(term: string): term is GlossaryTerm {
    return term in GlossaryService.TERMS;
  }
}